import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import styled from 'styled-components';
import Grow from '@material-ui/core/Grow';
import { useSelector } from 'react-redux';

import {
  APP, FETCH_ASSETS_URL,
} from 'appenv';
import { selectLocale } from 'models/localization';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import BusinessPopover from 'pages/booths/components/BusinessPopover';
import { Booth } from 'models/cms/booths';
import 'objectFitPolyfill';

import useEmailContactsMemo from '../useEmailContactsMemo';

const salesImg = `${FETCH_ASSETS_URL}/assets/booths/sales.png`;
const maleRight = `${FETCH_ASSETS_URL}/assets/booths/sale-male-right.png`;

const maleLeft = `${FETCH_ASSETS_URL}/assets/booths/sale-male-left.png`;

export const SaleInfoContainer = styled.div`
  position: absolute;
  left: -70px;
  bottom: 480px;
  width: 320px;
`;

export const SaleImg = styled.img<{ shouldEnableSalesRepClick: boolean }>`
  height: auto;
  max-height: 480px;
  position: absolute;
  top: 50%;
  cursor: ${({ shouldEnableSalesRepClick }) => (shouldEnableSalesRepClick ? 'pointer' : 'initial')};
  left: 50%;
  transform: translate(-50%, -50%);
  object-fit: contain;
`;

export const BubbleArrow = styled.div`
  height: 20px;
  width: 20px;
  position: absolute;
  bottom: -8px;
  left: 50%;
  background: #FFF;
  border-radius: 2px;
  box-shadow:
    3px 3px 3px -2px rgba(0, 0, 0, 0.2),
    3px 3px 4px 0 rgba(0, 0, 0, 0.14),
    1px 1px 8px -5px rgba(0, 0, 0, 0.12);
  transform: rotate(45deg) translate(-50%, 8px);
`;

const enableSalesRepClick = [];

export interface BoothSaleContainerProps {
  booth: Booth;
  sale?: string;
}

const BoothSaleContainer: React.FC<BoothSaleContainerProps> = ({
  booth, sale,
}: BoothSaleContainerProps) => {
  const [showRepsInfo, setShowRepsInfo] = useState(false);

  const shouldEnableSalesRepClick = useMemo(() => enableSalesRepClick.includes(APP), []);

  const onRepsClick = useCallback(() => {
    setShowRepsInfo(!showRepsInfo);
  }, [setShowRepsInfo, showRepsInfo]);

  useEffect(() => {
    setTimeout(() => {
      setShowRepsInfo(true);
    }, 500);
  }, []);

  useEffect(() => {
    window.objectFitPolyfill();
  }, []);

  const locale = useSelector(selectLocale);

  const representative = useMemo(
    () => booth.designElements?.find((it) => ['right_representative', 'representative'].includes(it?.name))?.image?.[locale]?.originalImageUrl,
    [booth.designElements, locale],
  );

  const renderedSalesImg = useMemo(() => {
    if (representative) return representative;
    switch (sale) {
      case 'male-left':
        return maleLeft;
      case 'sales':
        return salesImg;
      default:
        return maleRight;
    }
  }, [sale, representative]);

  const tabs = booth?.rootContentElement?.tabs || [];
  const emailContacts = useEmailContactsMemo(tabs);

  return (
    <>
      <SaleImg
        key={renderedSalesImg}
        src={renderedSalesImg}
        shouldEnableSalesRepClick={shouldEnableSalesRepClick}
        onClick={shouldEnableSalesRepClick ? onRepsClick : undefined}
      />
      <Grow
        in={showRepsInfo}
        timeout={800}
        style={{ transformOrigin: 'bottom' }}
      >
        <SaleInfoContainer>
          <BusinessPopover
            boothId={booth.id}
            boothName={findAvailableLocale(booth.name, locale)}
            emailContacts={emailContacts}
            meetupUrl={booth.meetupEnabled ? booth.appointmentBookingUrl : ''}
            livestreams={booth.livestreams}
            videoCallUrl={booth.videoCallUrl}
          />
          <BubbleArrow className="BubbleArrow" />
        </SaleInfoContainer>
      </Grow>
    </>
  );
};

export default BoothSaleContainer;
